import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navbar } from '../components/Navbar';
import { Upload, Folder, Search, Trash2, Globe, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp, Play, Users, Clock, RotateCcw, HardDrive, RefreshCw, AlertTriangle, Key, Box } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: fetch with auto-refresh on 401
async function fetchWithRefresh(url, options = {}) {
  let resp = await fetch(url, options);
  if (resp.status === 401) {
    try {
      await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
      resp = await fetch(url, options);
    } catch {
      // refresh failed, return original 401
    }
  }
  return resp;
}

// Global concurrent image loader — max 3 simultaneous loads
const imageQueue = { active: 0, max: 3, queue: [] };
function enqueueImageLoad(callback) {
  if (imageQueue.active < imageQueue.max) {
    imageQueue.active++;
    callback().finally(() => {
      imageQueue.active--;
      if (imageQueue.queue.length > 0) {
        const next = imageQueue.queue.shift();
        enqueueImageLoad(next);
      }
    });
  } else {
    imageQueue.queue.push(callback);
  }
}

// Lazy image component — only loads when scrolled into view, with concurrency control
function LazyImage({ src, alt, className }) {
  const imgRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    let cancelled = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setLoading(true);
          enqueueImageLoad(() =>
            fetchWithRefresh(src, { credentials: 'include' })
              .then(r => {
                if (!r.ok) throw new Error(r.status);
                return r.blob();
              })
              .then(blob => {
                if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
              })
              .catch(() => {})
              .finally(() => { if (!cancelled) setLoading(false); })
          );
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(el);
    return () => { cancelled = true; observer.disconnect(); };
  }, [src]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  return (
    <div ref={imgRef} className={className} style={{ backgroundColor: '#e5e7eb', position: 'relative' }}>
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className="w-full h-full object-cover" />
      ) : loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        </div>
      ) : null}
    </div>
  );
}

export function ImageLibraryPage() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('General');
  const { toast } = useToast();
  const { activeClient } = useClient();
  const [showScraper, setShowScraper] = useState(true);
  const [scrapeNames, setScrapeNames] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeResults, setScrapeResults] = useState(null);
  const [scrapeProgress, setScrapeProgress] = useState(null); // {total, items: [{name, status, website, images_saved, error}]}
  const [uploadProgress, setUploadProgress] = useState(null); // {current, total, fileName, percent}
  const [showAllClients, setShowAllClients] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [scrapeHistory, setScrapeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [usageFilter, setUsageFilter] = useState('all'); // all, never, recent, old
  const [showGDrive, setShowGDrive] = useState(false);
  const [gdriveUrl, setGdriveUrl] = useState('');
  const [gdriveApiKey, setGdriveApiKey] = useState('');
  const [gdriveHasKey, setGdriveHasKey] = useState(false);
  const [gdriveShowKeyInput, setGdriveShowKeyInput] = useState(false);
  const [gdriveImporting, setGdriveImporting] = useState(false);
  const [gdriveProgress, setGdriveProgress] = useState(null);
  const [gdriveCategory, setGdriveCategory] = useState('Google Drive');
  const [isSyncingUsage, setIsSyncingUsage] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);  // [{file, status, progress, name}]
  const [bulkCategory, setBulkCategory] = useState('Uploads');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const bulkInputRef = useRef(null);
  const [showDropbox, setShowDropbox] = useState(false);
  const [dropboxUrl, setDropboxUrl] = useState('');
  const [dropboxImporting, setDropboxImporting] = useState(false);
  const [dropboxProgress, setDropboxProgress] = useState(null);
  const [dropboxCategory, setDropboxCategory] = useState('Dropbox');
  const dropboxAbortRef = useRef(null);
  const gdriveAbortRef = useRef(null);

  const loadImages = useCallback(async (allClients = false) => {
    if (!activeClient) return;
    setIsLoading(true);
    try {
      const qs = allClients ? '?all_clients=true' : '';
      const resp = await axios.get(`${API}/library/images${qs}`, {
        withCredentials: true,
        headers: { 'X-Client-Id': activeClient.slug || activeClient.id }
      });
      const data = resp.data;
      setImages(data.images || []);
      const uniqueCategories = [...new Set(data.images?.map(img => img.category) || [])];
      setCategories(uniqueCategories.length > 0 ? uniqueCategories : ['General']);
    } catch (error) {
      console.error('Failed to load images:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeClient]);

  // Reload images when the active client changes
  useEffect(() => {
    if (activeClient) {
      setSelectedCategory('All');
      setSearchQuery('');
      setShowAllClients(false);
      loadImages(false);
      loadScrapeHistory();
    }
  }, [activeClient, loadImages]);

  const loadScrapeHistory = async () => {
    if (!activeClient) return;
    try {
      const resp = await axios.get(`${API}/scrape/history`, {
        withCredentials: true,
        headers: { 'X-Client-Id': activeClient.slug || activeClient.id }
      });
      setScrapeHistory(resp.data.history || []);
    } catch { /* ignore */ }
  };

  // Check if Google API key is saved
  useEffect(() => {
    axios.get(`${API}/library/import/gdrive/has-key`, { withCredentials: true })
      .then(r => setGdriveHasKey(r.data.has_key))
      .catch(() => {});
  }, []);

  const handleSyncUsage = async () => {
    setIsSyncingUsage(true);
    try {
      const resp = await axios.post(`${API}/library/sync-usage`, {}, { withCredentials: true });
      toast({ title: 'Usage synced', description: resp.data.message });
      loadImages(showAllClients);
    } catch {
      toast({ title: 'Sync failed', variant: 'destructive' });
    } finally {
      setIsSyncingUsage(false);
    }
  };

  const handleSaveGdriveKey = async () => {
    if (!gdriveApiKey.trim()) return;
    try {
      await axios.post(`${API}/library/import/gdrive/save-key`, { api_key: gdriveApiKey }, { withCredentials: true });
      setGdriveHasKey(true);
      setGdriveShowKeyInput(false);
      toast({ title: 'API key saved' });
    } catch {
      toast({ title: 'Failed to save key', variant: 'destructive' });
    }
  };

  const handleGdriveImport = async () => {
    if (!gdriveUrl.trim()) return;
    setGdriveImporting(true);
    setGdriveProgress(null);
    const abortController = new AbortController();
    gdriveAbortRef.current = abortController;
    
    try {
      const clientHeader = activeClient?.slug || activeClient?.id || '';
      const resp = await fetch(`${API}/library/import/gdrive`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Client-Id': clientHeader },
        body: JSON.stringify({ folder_url: gdriveUrl, category: gdriveCategory, api_key: gdriveApiKey || '' }),
        signal: abortController.signal,
      });
      
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'status') {
              setGdriveProgress({ status: evt.message });
            } else if (evt.type === 'progress') {
              setGdriveProgress({ current: evt.current, total: evt.total, name: evt.name });
            } else if (evt.type === 'complete') {
              setGdriveProgress({ complete: true, saved: evt.saved, errors: evt.errors, total: evt.total });
              toast({ title: 'Import complete', description: `${evt.saved} images imported from Google Drive` });
              loadImages(showAllClients);
            } else if (evt.type === 'error') {
              setGdriveProgress({ error: evt.message });
              toast({ title: 'Import failed', description: evt.message, variant: 'destructive' });
            }
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setGdriveProgress({ error: 'Import cancelled.' });
        toast({ title: 'Import cancelled' });
      } else {
        toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
      }
    } finally {
      gdriveAbortRef.current = null;
      setGdriveImporting(false);
    }
  };

  // Dropbox handler — background job with polling (state in MongoDB, not memory)
  const handleDropboxImport = async () => {
    if (!dropboxUrl.trim()) return;
    setDropboxImporting(true);
    setDropboxProgress({ status: 'Starting import...' });

    try {
      const clientHeader = activeClient?.slug || activeClient?.id || '';

      // 1. Start the background import job
      const startResp = await fetch(`${API}/library/import/dropbox/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Client-Id': clientHeader },
        body: JSON.stringify({ folder_url: dropboxUrl, category: dropboxCategory }),
      });

      if (!startResp.ok) {
        const errData = await startResp.json().catch(() => ({}));
        const msg = errData.error || errData.detail || `Server error (${startResp.status})`;
        setDropboxProgress({ error: msg });
        toast({ title: 'Import failed', description: msg, variant: 'destructive' });
        return;
      }

      const startData = await startResp.json();
      if (startData.error) {
        setDropboxProgress({ error: startData.error });
        toast({ title: 'Import failed', description: startData.error, variant: 'destructive' });
        return;
      }

      const jobId = startData.job_id;
      let consecutiveErrors = 0;

      // 2. Poll for status every 3 seconds
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusResp = await fetch(`${API}/library/import/dropbox/status/${jobId}`, {
            credentials: 'include',
            headers: { 'X-Client-Id': clientHeader },
          });

          // Handle 404 or non-OK responses
          if (!statusResp.ok) {
            const errData = await statusResp.json().catch(() => ({}));
            const msg = errData.error || 'Import session lost — please retry.';
            setDropboxProgress({ error: msg });
            toast({ title: 'Import lost', description: msg, variant: 'destructive' });
            return;
          }

          const s = await statusResp.json();
          consecutiveErrors = 0; // reset on success

          // Handle missing state gracefully
          if (!s.state) {
            setDropboxProgress({ error: 'Import session lost — please retry.' });
            toast({ title: 'Import lost', description: 'Could not read import status.', variant: 'destructive' });
            return;
          }

          if (s.state === 'error') {
            setDropboxProgress({ error: s.error || 'Unknown import error' });
            toast({ title: 'Import failed', description: s.error || 'Unknown error', variant: 'destructive' });
            return;
          }

          if (s.state === 'complete') {
            setDropboxProgress({ complete: true, saved: s.saved, errors: s.errors, total: s.file_total });
            toast({ title: 'Import complete', description: `${s.saved} images imported from Dropbox` });
            loadImages(showAllClients);
            return;
          }

          // Still running — always show a meaningful message
          if (s.file_total > 0 && s.file_current > 0) {
            setDropboxProgress({
              current: s.file_current,
              total: s.file_total,
              name: s.file_name || `File ${s.file_current}`,
              status: s.status_msg || `Processing file ${s.file_current} of ${s.file_total}...`,
              saved: s.saved,
            });
          } else if (s.download_total > 0 && s.download_bytes > 0) {
            const mb = Math.round(s.download_bytes / (1024 * 1024));
            const pct = Math.min(99, Math.round(s.download_bytes / s.download_total * 100));
            setDropboxProgress({ status: `Downloading... ${mb}MB (${pct}%)` });
          } else {
            setDropboxProgress({ status: s.status_msg || 'Preparing import...' });
          }
        } catch (networkErr) {
          // Network blip — tolerate a few, then give up
          consecutiveErrors++;
          if (consecutiveErrors >= 5) {
            setDropboxProgress({ error: 'Lost connection to server. The import may still be running — refresh the page to check.' });
            toast({ title: 'Connection lost', description: 'Could not reach the server.', variant: 'destructive' });
            return;
          }
          setDropboxProgress(prev => ({ ...prev, status: `Reconnecting... (attempt ${consecutiveErrors}/5)` }));
        }
      }

    } catch (err) {
      setDropboxProgress({ error: err.message || 'Unexpected error' });
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
    } finally {
      dropboxAbortRef.current = null;
      setDropboxImporting(false);
    }
  };

  // ── Bulk multi-file uploader ──
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
  const ACCEPTED_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];

  const addBulkFiles = (fileList) => {
    const newFiles = Array.from(fileList)
      .filter(f => {
        const ext = '.' + f.name.split('.').pop().toLowerCase();
        return ACCEPTED_EXTS.includes(ext) || ACCEPTED_TYPES.includes(f.type);
      })
      .map(f => ({ file: f, name: f.name, status: 'pending', progress: 0 }));
    setBulkFiles(prev => [...prev, ...newFiles]);
  };

  const removeBulkFile = (idx) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    const clientHeader = activeClient?.slug || activeClient?.id || '';

    for (let i = 0; i < bulkFiles.length; i++) {
      const entry = bulkFiles[i];
      if (entry.status === 'done') continue;

      setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading', progress: 0 } : f));

      try {
        const formData = new FormData();
        formData.append('file', entry.file);
        formData.append('category', bulkCategory);

        const resp = await axios.post(`${API}/library/upload`, formData, {
          withCredentials: true,
          headers: { 'X-Client-Id': clientHeader },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / e.total) * 100);
            setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: pct } : f));
          },
        });

        setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done', progress: 100 } : f));
      } catch (err) {
        const msg = err.response?.data?.detail || err.message;
        setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: msg } : f));
      }
    }

    setBulkUploading(false);
    const doneCount = bulkFiles.filter(f => f.status !== 'error').length;
    toast({ title: 'Upload complete', description: `${doneCount} images uploaded` });
    loadImages(showAllClients);
  };

  const handleRescrape = (entry) => {
    setScrapeNames(entry.names.join('\n'));
    setShowScraper(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast({ title: 'Names loaded', description: `${entry.names.length} business names ready to re-scrape` });
  };

  const handleDeleteHistory = async (historyId) => {
    try {
      const clientHeader = activeClient?.slug || activeClient?.id || '';
      await axios.delete(`${API}/scrape/history/${historyId}`, {
        withCredentials: true,
        headers: { 'X-Client-Id': clientHeader }
      });
      setScrapeHistory(prev => prev.filter(h => h.id !== historyId));
      toast({ title: 'History entry removed' });
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleToggleAllClients = () => {
    const next = !showAllClients;
    setShowAllClients(next);
    setSelectedCategory('All');
    setSearchQuery('');
    setVisibleCount(10);
    setImages([]); // Clear images so browser aborts pending image downloads
    setIsLoading(true);
    // Give React a tick to remove img elements from DOM, then fetch
    setTimeout(() => loadImages(next), 100);
  };

  // Chunk size: 4MB per chunk (well under proxy limits)
  const CHUNK_SIZE = 4 * 1024 * 1024;

  const uploadFileChunked = async (file, category) => {
    const clientHeader = activeClient?.slug || activeClient?.id || '';
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // 1. Init upload session
    const initResp = await axios.post(`${API}/library/upload/init`, {
      filename: file.name,
      totalSize: file.size,
      totalChunks,
      category,
    }, { withCredentials: true, headers: { 'X-Client-Id': clientHeader } });
    const { upload_id } = initResp.data;

    // 2. Upload each chunk
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, `chunk_${i}`);

      await axios.post(`${API}/library/upload/chunk/${upload_id}?chunk_index=${i}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data', 'X-Client-Id': clientHeader },
        timeout: 120000,
      });

      const pct = Math.round(((i + 1) / totalChunks) * 90); // 0-90% for chunks
      setUploadProgress(prev => ({ ...prev, percent: pct }));
    }

    // 3. Kick off background assembly
    setUploadProgress(prev => ({ ...prev, percent: 92, assembling: true }));
    await axios.post(`${API}/library/upload/complete/${upload_id}`, {}, {
      withCredentials: true,
      headers: { 'X-Client-Id': clientHeader },
      timeout: 30000,
    });

    // 4. Poll for completion
    const pollInterval = 2000;
    const maxPolls = 300; // 10 min max
    for (let p = 0; p < maxPolls; p++) {
      await new Promise(r => setTimeout(r, pollInterval));
      try {
        const statusResp = await axios.get(`${API}/library/upload/status/${upload_id}`, {
          withCredentials: true,
          headers: { 'X-Client-Id': clientHeader },
          timeout: 15000,
        });
        const st = statusResp.data;
        if (st.status === 'completed') {
          setUploadProgress(prev => ({ ...prev, percent: 100, assembling: false }));
          return st;
        }
        if (st.status === 'failed') {
          throw new Error(st.error || 'Assembly failed');
        }
        // Still processing — tick the progress bar slowly
        const tick = Math.min(92 + Math.floor((p / maxPolls) * 8), 99);
        setUploadProgress(prev => ({ ...prev, percent: tick }));
      } catch (pollErr) {
        if (pollErr.response?.status === 404) {
          // Session gone — might have been cleaned up after completion on a fast path
          setUploadProgress(prev => ({ ...prev, percent: 100, assembling: false }));
          return {};
        }
        throw pollErr;
      }
    }
    throw new Error('Upload processing timed out after 10 minutes');
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      setUploadProgress({ current: i + 1, total: files.length, fileName: file.name, percent: 0, sizeMB });

      try {
        const clientHeader = activeClient?.slug || activeClient?.id || '';
        // Use chunked upload for files > 8MB, direct upload for smaller
        if (file.size > 8 * 1024 * 1024) {
          await uploadFileChunked(file, uploadCategory);
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', uploadCategory);

          await axios.post(`${API}/library/upload`, formData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data', 'X-Client-Id': clientHeader },
            timeout: 600000,
            onUploadProgress: (progressEvent) => {
              const pct = progressEvent.total ? Math.round((progressEvent.loaded / progressEvent.total) * 100) : 0;
              setUploadProgress(prev => ({ ...prev, percent: pct }));
            }
          });
        }
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        const detail = error.response?.data?.detail || error.message;
        toast({ title: `Failed: ${file.name}`, description: detail, variant: "destructive" });
      }
    }

    toast({
      title: "Upload complete",
      description: `${successCount} of ${files.length} files uploaded to ${uploadCategory}`
    });

    setIsUploading(false);
    setUploadProgress(null);
    loadImages(showAllClients);
    e.target.value = '';
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const clientHeader = activeClient?.slug || activeClient?.id || '';
      await axios.delete(`${API}/library/images/${imageId}`, {
        withCredentials: true,
        headers: { 'X-Client-Id': clientHeader }
      });
      toast({ title: "Image deleted" });
      loadImages();
    } catch (error) {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const handleScrape = async () => {
    const names = scrapeNames.split('\n').map(n => n.trim()).filter(Boolean);
    if (names.length === 0) {
      toast({ title: "No names entered", description: "Paste one business name per line", variant: "destructive" });
      return;
    }
    setIsScraping(true);
    setScrapeResults(null);
    setScrapeProgress({ total: names.length, items: names.map(n => ({ name: n, status: 'pending' })) });

    try {
      const clientHeader = activeClient?.slug || activeClient?.id || '';
      const resp = await fetch(`${API}/scrape/images/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Client-Id': clientHeader },
        body: JSON.stringify({ names, images_per_name: 8 })
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));

            if (evt.type === 'progress') {
              setScrapeProgress(prev => {
                if (!prev) return prev;
                const items = [...prev.items];
                items[evt.index] = {
                  name: evt.name,
                  status: evt.status,
                  website: evt.website || null,
                  images_saved: evt.images_saved || 0,
                  error: evt.error || null,
                };
                return { ...prev, items };
              });
            } else if (evt.type === 'complete') {
              setScrapeResults(evt);
              toast({
                title: 'Scraping complete',
                description: `${evt.total_saved} images saved from ${evt.results.filter(r => r.status === 'done').length} of ${evt.total_names} businesses`
              });
              loadImages();
              loadScrapeHistory();
            }
          } catch { /* skip malformed lines */ }
        }
      }
    } catch (error) {
      toast({ title: "Scraping failed", description: error.message, variant: "destructive" });
    } finally {
      setIsScraping(false);
    }
  };

  const filteredImages = images.filter(img => {
    const matchesCategory = selectedCategory === 'All' || img.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      img.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      img.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (showAllClients && img.client_name?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Usage filter
    let matchesUsage = true;
    if (usageFilter === 'never') {
      matchesUsage = !img.use_count || img.use_count === 0;
    } else if (usageFilter === 'recent') {
      if (!img.last_used_at) { matchesUsage = false; }
      else {
        const usedDate = new Date(img.last_used_at);
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        matchesUsage = usedDate >= twelveMonthsAgo;
      }
    } else if (usageFilter === 'old') {
      if (!img.last_used_at) { matchesUsage = false; }
      else {
        const usedDate = new Date(img.last_used_at);
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        matchesUsage = usedDate < twelveMonthsAgo;
      }
    }
    
    return matchesCategory && matchesSearch && matchesUsage;
  });

  const displayedImages = filteredImages.slice(0, visibleCount);
  const hasMore = filteredImages.length > visibleCount;

  return (
    <div className="min-h-screen page-background" data-testid="image-library-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: 'var(--brand-accent)' }}>Asset Management</p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ color: 'var(--brand-primary)' }}>
              Image Library
            </h1>
            <p className="text-gray-500 mt-1">
              {images.length} image{images.length !== 1 ? 's' : ''} {showAllClients ? 'across all clients' : <>for <span className="font-semibold">{activeClient?.name || 'this client'}</span></>}
            </p>
          </div>
          <button
            onClick={handleToggleAllClients}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border ${
              showAllClients
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
            style={showAllClients ? { backgroundColor: 'var(--brand-primary)' } : {}}
            data-testid="toggle-all-clients-library"
          >
            <Users className="w-4 h-4" />
            {showAllClients ? 'All Clients' : 'All Clients'}
          </button>
        </div>

        {/* Upload Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Upload Images</h3>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 block">
                Category
              </label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-gray-900"
                data-testid="upload-category-select"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="General">General</option>
              </select>
            </div>
            <div>
              <label className="cursor-pointer">
                <span
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  <Upload className="w-4 h-4" />
                  {isUploading ? 'Uploading...' : 'Choose Files'}
                </span>
                <input
                  type="file"
                  accept="image/*,video/mp4,video/quicktime,video/webm,.heic,.heif,.mov"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  data-testid="library-file-input"
                />
              </label>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Upload images and videos. Supported: JPG, PNG, GIF, WebP, HEIC/HEIF, MP4, MOV, WebM — up to 200MB per file
          </p>
          {/* Upload Progress */}
          {uploadProgress && (
            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-blue-800 truncate max-w-[250px]">
                  {uploadProgress.assembling ? '⏳ Processing on server...' : uploadProgress.fileName}
                </span>
                <span className="text-xs text-blue-600">
                  {uploadProgress.current}/{uploadProgress.total} • {uploadProgress.sizeMB}MB • {uploadProgress.percent}%
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.percent}%`, backgroundColor: 'var(--brand-primary)' }}
                />
              </div>
              {uploadProgress.assembling && (
                <p className="text-xs text-blue-600 mt-1.5">Assembling file and generating thumbnail — this runs in the background, no timeout risk.</p>
              )}
            </div>
          )}
        </div>

        {/* Web Image Scraper */}
        <div className="bg-white border border-gray-200 rounded-xl mb-8 overflow-hidden">
          <button
            onClick={() => setShowScraper(!showScraper)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-scraper-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--brand-primary)', opacity: 0.1 }}>
                <Globe className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Scrape from Web</h3>
                <p className="text-xs text-gray-500">Paste business names or website URLs — we'll find their sites and grab photos</p>
              </div>
            </div>
            {showScraper ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showScraper && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
              <Textarea
                value={scrapeNames}
                onChange={(e) => setScrapeNames(e.target.value)}
                placeholder={"Paste business names or URLs, one per line:\n\nhttps://bellaterrallc.com\nNaked Mountain Vineyard\nNarmada Winery LLC\nnewkentwinery.com"}
                rows={8}
                className="font-mono text-sm resize-none"
                disabled={isScraping}
                data-testid="scrape-names-input"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {scrapeNames.split('\n').filter(n => n.trim()).length} business{scrapeNames.split('\n').filter(n => n.trim()).length !== 1 ? 'es' : ''} • ~5-10 images each • saved to {activeClient?.name || 'current client'}'s library
                </p>
                <Button
                  onClick={handleScrape}
                  disabled={isScraping || !scrapeNames.trim()}
                  className="text-white"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                  data-testid="start-scrape-btn"
                >
                  {isScraping ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Scraping...
                    </>
                  ) : (
                    <>
                      <Globe className="w-4 h-4 mr-2" />
                      Scrape Images
                    </>
                  )}
                </Button>
              </div>

              {/* Live Progress */}
              {isScraping && scrapeProgress && !scrapeResults && (
                <div className="space-y-2" data-testid="scrape-live-progress">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">
                      {scrapeProgress.items.filter(i => i.status === 'done' || i.status === 'error').length} / {scrapeProgress.total} complete
                    </span>
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${(scrapeProgress.items.filter(i => ['done','error'].includes(i.status)).length / scrapeProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                  {scrapeProgress.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-gray-50 text-sm">
                      {item.status === 'pending' ? (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                      ) : item.status === 'searching' ? (
                        <Loader2 className="w-4 h-4 text-amber-500 animate-spin flex-shrink-0" />
                      ) : item.status === 'scraping' || item.status === 'downloading' ? (
                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                      ) : item.status === 'done' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className={`font-medium flex-1 truncate ${item.status === 'pending' ? 'text-gray-400' : 'text-gray-700'}`}>
                        {item.name}
                      </span>
                      {item.status === 'searching' && (
                        <span className="text-xs text-amber-600">Finding website...</span>
                      )}
                      {item.status === 'scraping' && (
                        <span className="text-xs text-blue-600">Searching for images...</span>
                      )}
                      {item.status === 'downloading' && (
                        <span className="text-xs text-indigo-600">Downloading & deduplicating...</span>
                      )}
                      {item.status === 'done' && (
                        <span className="text-xs text-green-600 font-medium">{item.images_saved} images</span>
                      )}
                      {item.status === 'error' && (
                        <span className="text-xs text-red-500">{item.error}</span>
                      )}
                      {item.website && (
                        <a href={item.website} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate max-w-[120px]">
                          {(() => { try { return new URL(item.website).hostname; } catch { return ''; } })()}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {scrapeResults && (
                <div className="space-y-2" data-testid="scrape-results">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-semibold text-gray-800">
                      {scrapeResults.total_saved} images saved from {scrapeResults.results.filter(r => r.status === 'done').length} of {scrapeResults.total_names} businesses
                    </span>
                  </div>
                  {scrapeResults.results.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-gray-50 text-sm">
                      {r.status === 'done' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-700 flex-1 truncate">{r.name}</span>
                      {r.status === 'done' ? (
                        <span className="text-xs text-green-600 font-medium">{r.images_saved} images</span>
                      ) : (
                        <span className="text-xs text-red-500">{r.error}</span>
                      )}
                      {r.website && (
                        <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate max-w-[120px]">
                          {new URL(r.website).hostname}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrape History */}
        {scrapeHistory.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl mb-8 overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
              data-testid="toggle-history-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100">
                  <Clock className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Scrape History</h3>
                  <p className="text-xs text-gray-500">{scrapeHistory.length} previous scrape session{scrapeHistory.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              {showHistory ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {showHistory && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-3 space-y-2" data-testid="scrape-history-list">
                {scrapeHistory.map(entry => {
                  const successCount = (entry.results || []).filter(r => r.status === 'done').length;
                  const date = entry.created_at ? new Date(entry.created_at) : null;
                  return (
                    <div
                      key={entry.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                      data-testid={`history-entry-${entry.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {entry.names.slice(0, 3).join(', ')}{entry.names.length > 3 ? ` +${entry.names.length - 3} more` : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          {date && <span>{date.toLocaleDateString()} at {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                          <span>{entry.total_names} business{entry.total_names !== 1 ? 'es' : ''}</span>
                          <span className="text-green-600 font-medium">{entry.total_saved} images saved</span>
                          {successCount < entry.total_names && (
                            <span className="text-amber-600">{entry.total_names - successCount} failed</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRescrape(entry)}
                          className="h-7 text-xs gap-1 border-gray-200"
                          data-testid={`rescrape-${entry.id}`}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Re-scrape
                        </Button>
                        <button
                          onClick={() => handleDeleteHistory(entry.id)}
                          className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          data-testid={`delete-history-${entry.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Bulk Upload (Drag & Drop) ── */}
        <div className="bg-white border border-gray-200 rounded-xl mb-8 overflow-hidden">
          <button
            onClick={() => setShowBulkUpload(!showBulkUpload)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-bulk-upload-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-50">
                <Upload className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Upload Files</h3>
                <p className="text-xs text-gray-500">Drag & drop or select multiple images at once</p>
              </div>
            </div>
            {showBulkUpload ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showBulkUpload && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
              {/* Category */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Category</label>
                <Input
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                  placeholder="e.g. Uploads"
                  className="max-w-xs"
                  data-testid="bulk-upload-category"
                />
              </div>

              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setBulkDragOver(true); }}
                onDragLeave={() => setBulkDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setBulkDragOver(false); addBulkFiles(e.dataTransfer.files); }}
                onClick={() => !bulkUploading && bulkInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  bulkDragOver
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
                data-testid="bulk-upload-dropzone"
              >
                <input
                  ref={bulkInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_EXTS.join(',')}
                  onChange={(e) => { addBulkFiles(e.target.files); e.target.value = ''; }}
                  className="hidden"
                  data-testid="bulk-upload-input"
                />
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">
                  {bulkDragOver ? 'Drop images here' : 'Drag & drop images, or click to browse'}
                </p>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP, HEIC — no size limit on number of files</p>
              </div>

              {/* File list */}
              {bulkFiles.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{bulkFiles.length} file{bulkFiles.length !== 1 ? 's' : ''} selected</span>
                    {!bulkUploading && (
                      <button
                        onClick={() => setBulkFiles([])}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                        data-testid="bulk-clear-all"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                    {bulkFiles.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                        <span className="truncate flex-1 text-gray-700 max-w-[220px]" title={entry.name}>{entry.name}</span>
                        <span className="text-xs text-gray-400 shrink-0">{(entry.file.size / (1024 * 1024)).toFixed(1)}MB</span>
                        {entry.status === 'pending' && !bulkUploading && (
                          <button onClick={() => removeBulkFile(idx)} className="text-gray-300 hover:text-red-500">
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {entry.status === 'uploading' && (
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden shrink-0">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${entry.progress}%` }} />
                          </div>
                        )}
                        {entry.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                        {entry.status === 'error' && (
                          <span className="text-xs text-red-500 shrink-0" title={entry.error}>Failed</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload button */}
              {bulkFiles.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleBulkUpload}
                    disabled={bulkUploading || bulkFiles.every(f => f.status === 'done')}
                    className="text-white"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                    data-testid="bulk-upload-start-btn"
                  >
                    {bulkUploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading {bulkFiles.filter(f => f.status === 'done').length}/{bulkFiles.length}...</>
                    ) : bulkFiles.every(f => f.status === 'done') ? (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />All uploaded</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" />Upload {bulkFiles.filter(f => f.status === 'pending').length} files</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Google Drive Import */}
        <div className="bg-white border border-gray-200 rounded-xl mb-8 overflow-hidden">
          <button
            onClick={() => setShowGDrive(!showGDrive)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-gdrive-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
                <HardDrive className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Import from Google Drive</h3>
                <p className="text-xs text-gray-500">Paste a shared folder link to import all images</p>
              </div>
            </div>
            {showGDrive ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showGDrive && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
              {/* API Key Setup */}
              {!gdriveHasKey && !gdriveShowKeyInput && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="gdrive-key-notice">
                  <Key className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 mb-1">Google API Key Required</p>
                    <p className="text-xs text-amber-700 mb-2">
                      You need a free Google API key to import from Drive. 
                      <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline ml-1">Get one here</a> — enable the Google Drive API in your project.
                    </p>
                    <Button size="sm" variant="outline" onClick={() => setGdriveShowKeyInput(true)} className="text-amber-700 border-amber-300 hover:bg-amber-100">
                      Add API Key
                    </Button>
                  </div>
                </div>
              )}

              {(gdriveShowKeyInput || (!gdriveHasKey && gdriveShowKeyInput)) && (
                <div className="flex gap-2 items-end" data-testid="gdrive-key-input">
                  <div className="flex-1">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Google API Key</label>
                    <Input
                      type="password"
                      value={gdriveApiKey}
                      onChange={(e) => setGdriveApiKey(e.target.value)}
                      placeholder="AIza..."
                      data-testid="gdrive-api-key-input"
                    />
                  </div>
                  <Button onClick={handleSaveGdriveKey} className="text-white" style={{ backgroundColor: 'var(--brand-primary)' }} data-testid="gdrive-save-key-btn">
                    Save Key
                  </Button>
                </div>
              )}

              {gdriveHasKey && (
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Google API key is configured
                  <button onClick={() => setGdriveShowKeyInput(true)} className="text-green-600 hover:text-green-800 underline ml-auto">Update</button>
                </div>
              )}

              {/* Folder URL Input */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">
                    Shared Folder URL
                  </label>
                  <Input
                    value={gdriveUrl}
                    onChange={(e) => setGdriveUrl(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/1abc..."
                    disabled={gdriveImporting}
                    data-testid="gdrive-folder-url-input"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Category</label>
                    <Input
                      value={gdriveCategory}
                      onChange={(e) => setGdriveCategory(e.target.value)}
                      placeholder="Google Drive"
                      disabled={gdriveImporting}
                      data-testid="gdrive-category-input"
                    />
                  </div>
                  <Button
                    onClick={handleGdriveImport}
                    disabled={gdriveImporting || !gdriveUrl.trim() || (!gdriveHasKey && !gdriveApiKey.trim())}
                    className="text-white"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                    data-testid="gdrive-import-btn"
                  >
                    {gdriveImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <HardDrive className="w-4 h-4 mr-2" />
                        Import Images
                      </>
                    )}
                  </Button>
                  {gdriveImporting && (
                    <Button
                      variant="destructive"
                      onClick={() => gdriveAbortRef.current?.abort()}
                      className="shrink-0"
                      data-testid="gdrive-stop-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />Stop
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  The folder must be shared as "Anyone with the link can view". Only image files (JPG, PNG, GIF, WebP) will be imported.
                </p>
              </div>

              {/* Import Progress */}
              {gdriveProgress && (
                <div className="space-y-2" data-testid="gdrive-progress">
                  {gdriveProgress.status && !gdriveProgress.complete && !gdriveProgress.error && !gdriveProgress.current && (
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {gdriveProgress.status}
                    </div>
                  )}
                  {gdriveProgress.current && !gdriveProgress.complete && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[250px]">{gdriveProgress.name}</span>
                        <span className="text-xs text-gray-500">{gdriveProgress.current}/{gdriveProgress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-300 bg-blue-500"
                          style={{ width: `${(gdriveProgress.current / gdriveProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {gdriveProgress.complete && (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                      <CheckCircle2 className="w-4 h-4" />
                      Imported {gdriveProgress.saved} of {gdriveProgress.total} images
                      {gdriveProgress.errors > 0 && <span className="text-amber-600">({gdriveProgress.errors} skipped)</span>}
                    </div>
                  )}
                  {gdriveProgress.error && (
                    <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {gdriveProgress.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dropbox Import */}
        <div className="bg-white border border-gray-200 rounded-xl mb-8 overflow-hidden">
          <button
            onClick={() => setShowDropbox(!showDropbox)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-dropbox-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50">
                <Box className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Import from Dropbox</h3>
                <p className="text-xs text-gray-500">Paste a shared Dropbox folder link to import images</p>
              </div>
            </div>
            {showDropbox ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showDropbox && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
              {/* Folder URL Input */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">
                    Shared Folder URL
                  </label>
                  <Input
                    value={dropboxUrl}
                    onChange={(e) => setDropboxUrl(e.target.value)}
                    placeholder="https://www.dropbox.com/scl/fo/..."
                    disabled={dropboxImporting}
                    data-testid="dropbox-folder-url-input"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Category</label>
                    <Input
                      value={dropboxCategory}
                      onChange={(e) => setDropboxCategory(e.target.value)}
                      placeholder="Dropbox"
                      disabled={dropboxImporting}
                      data-testid="dropbox-category-input"
                    />
                  </div>
                  <Button
                    onClick={handleDropboxImport}
                    disabled={dropboxImporting || !dropboxUrl.trim()}
                    className="text-white"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                    data-testid="dropbox-import-btn"
                  >
                    {dropboxImporting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                    ) : (
                      <><Box className="w-4 h-4 mr-2" />Import Images</>
                    )}
                  </Button>
                  {dropboxImporting && (
                    <Button
                      variant="destructive"
                      onClick={() => dropboxAbortRef.current?.abort()}
                      className="shrink-0"
                      data-testid="dropbox-stop-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />Stop
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  Just paste the shared link from Dropbox — no account setup needed. The folder will be downloaded as a zip and all images will be extracted automatically.
                </p>
              </div>

              {/* Import Progress */}
              {dropboxProgress && (
                <div className="space-y-2" data-testid="dropbox-progress">
                  {dropboxProgress.status && !dropboxProgress.complete && !dropboxProgress.error && !dropboxProgress.current && (
                    <div className="flex items-center gap-2 text-sm text-indigo-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {dropboxProgress.status}
                    </div>
                  )}
                  {dropboxProgress.current && !dropboxProgress.complete && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[250px]">{dropboxProgress.name}</span>
                        <span className="text-xs text-gray-500">{dropboxProgress.current}/{dropboxProgress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all duration-300 bg-indigo-500" style={{ width: `${(dropboxProgress.current / dropboxProgress.total) * 100}%` }} />
                      </div>
                    </div>
                  )}
                  {dropboxProgress.complete && (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                      <CheckCircle2 className="w-4 h-4" />
                      Imported {dropboxProgress.saved} of {dropboxProgress.total} images
                      {dropboxProgress.errors > 0 && <span className="text-amber-600">({dropboxProgress.errors} skipped)</span>}
                    </div>
                  )}
                  {dropboxProgress.error && (
                    <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {dropboxProgress.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="library-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncUsage}
              disabled={isSyncingUsage}
              className="text-xs gap-1.5"
              data-testid="sync-usage-btn"
              title="Scan all posts to update which images have been used"
            >
              {isSyncingUsage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Sync Usage
            </Button>
          </div>
        </div>

        {/* Usage Filter Pills */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { key: 'all', label: 'All Images' },
            { key: 'never', label: 'Never Used' },
            { key: 'recent', label: 'Used < 12 months' },
            { key: 'old', label: 'Used 12+ months ago' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setUsageFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                usageFilter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`usage-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          <button
            onClick={() => { setSelectedCategory('All'); setVisibleCount(20); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === 'All' ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
            style={selectedCategory === 'All' ? { backgroundColor: 'var(--brand-primary)' } : {}}
            data-testid="filter-all"
          >
            All ({images.length})
          </button>
          {categories.map(cat => {
            const count = images.filter(img => img.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => { setSelectedCategory(cat); setVisibleCount(20); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
                style={selectedCategory === cat ? { backgroundColor: 'var(--brand-primary)' } : {}}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Image Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
              <p className="text-gray-400 text-sm">Loading library...</p>
            </div>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
            <Folder className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {images.length === 0 ? 'No images yet' : 'No images match your filter'}
            </h3>
            <p className="text-gray-500 mb-4">
              Upload images to build {activeClient?.name || 'this client'}'s library
            </p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="library-image-grid">
            {displayedImages.map(image => {
              const isVideo = image.is_video || (image.content_type && image.content_type.startsWith('video/'));
              const useCount = image.use_count || 0;
              const lastUsed = image.last_used_at ? new Date(image.last_used_at) : null;
              const twelveMonthsAgo = new Date();
              twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
              const isRecentlyUsed = lastUsed && lastUsed >= twelveMonthsAgo;
              const sourceLabel = image.source === 'portal_upload' ? 'Client' : image.source === 'google_drive' ? 'GDrive' : null;
              return (
                <div
                  key={image.id}
                  className="group relative aspect-square rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all"
                  data-testid={`library-image-${image.id}`}
                >
                  {isVideo ? (
                    <LazyImage
                      src={image.thumbnail_url ? `${API}${image.thumbnail_url.startsWith('/api') ? image.thumbnail_url.replace('/api', '') : image.thumbnail_url}` : `${API}/library/images/${image.id}/thumbnail`}
                      alt={image.name}
                      className="w-full h-full"
                    />
                  ) : (
                    <LazyImage
                      src={`${API}/library/images/${image.id}/file`}
                      alt={image.name}
                      className="w-full h-full"
                    />
                  )}
                  {/* Video play badge */}
                  {isVideo && (
                    <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                      <Play className="w-3 h-3 text-white fill-white" />
                    </div>
                  )}
                  {/* Usage badge */}
                  {useCount > 0 && (
                    <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm ${
                      isRecentlyUsed ? 'bg-amber-500/90 text-white' : 'bg-green-500/90 text-white'
                    }`} data-testid={`usage-badge-${image.id}`} title={lastUsed ? `Last used: ${lastUsed.toLocaleDateString()}` : ''}>
                      {isRecentlyUsed ? `Used ${useCount}x` : `${useCount}x (12+mo)`}
                    </div>
                  )}
                  {/* Source badge */}
                  {sourceLabel && !isVideo && useCount === 0 && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/90 text-white backdrop-blur-sm">
                      {sourceLabel}
                    </div>
                  )}
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      data-testid={`delete-${image.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  {/* Info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-xs text-white truncate font-medium">{image.name}</p>
                    <p className="text-[10px] text-white/70">{image.category}{isVideo ? ' • Video' : ''}{showAllClients && image.client_name ? ` • ${image.client_name}` : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Load More button */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={() => setVisibleCount(prev => prev + 20)}
                variant="outline"
                className="px-8 py-3"
                data-testid="load-more-btn"
              >
                Load More ({filteredImages.length - visibleCount} remaining)
              </Button>
            </div>
          )}
          </>
        )}
      </main>
    </div>
  );
}
